/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import { Module, Node } from '@msdyn365-commerce-modules/utilities';
import * as React from 'react';

import {
    IBuyboxAddToCartViewProps,
    IBuyboxAddToWishlistViewProps,
    IBuyboxKeyInPriceViewProps,
    IBuyboxProductConfigureDropdownViewProps,
    IBuyboxProductConfigureViewProps,
    IBuyboxProductQuantityViewProps,
} from '@msdyn365-commerce-modules/buybox';

//----------------------------------------------------------
// @CUSTOM: Imports for customizations
//----------------------------------------------------------
import { clsHelper } from '../../utilities/class-name-helper';
import { labelResources, attrNames } from '../../utilities/global-constants';
import { convertProductAttributes, convertCustomerAttributes, AttributesWithMetadata } from '../../utilities/data-attribute-parser';

import { getCatalogId, RichTextComponent } from '@msdyn365-commerce/core';
import { searchByCriteriaAsync } from '@msdyn365-commerce/retail-proxy/dist/DataActions/ProductsDataActions.g';

import { IQuickviewViewProps } from './quickview';
import { IQuickviewProps } from './quickview.props.autogenerated';
import { AddToCartComponent } from '@msdyn365-commerce/components';
//----------------------------------------------------------

//----------------------------------------------------------
// @CUSTOM: Class name utility for customizations that
// want to use the same base class
//----------------------------------------------------------
const BASE_CLASS = 'ms-quickView';
const cls = (fragment?: string) => clsHelper(BASE_CLASS, fragment);
//----------------------------------------------------------

const _renderAddToCart = (addToCart: IBuyboxAddToCartViewProps): JSX.Element => {
    const { ContainerProps, errorBlock, button } = addToCart;
    const buttonProps = React.isValidElement(addToCart.button) && addToCart.button.props;

    return (
        <Node {...ContainerProps}>
            {errorBlock}
            {button && <AddToCartComponent {...buttonProps}/>}
        </Node>
    );
};

const _renderAddToWishlist = (addToWishlist: IBuyboxAddToWishlistViewProps): JSX.Element => {
    const { ContainerProps, errorBlock, button } = addToWishlist;

    return (
        <Node {...ContainerProps}>
            {errorBlock}
            {button}
        </Node>
    );
};

const _renderConfigureDropdown = (dropdown: IBuyboxProductConfigureDropdownViewProps): JSX.Element => {
    const { ContainerProps, LabelContainerProps, heading, errors, select } = dropdown;

    return (
        <Node {...ContainerProps}>
            <Node {...LabelContainerProps}>
                {heading}
                {errors}
            </Node>
            {select}
        </Node>
    );
};

const _renderQuickViewPopup = (props: IQuickviewViewProps & IQuickviewProps<{}>): JSX.Element => {  // @CUSTOM: Added extended definition props
    const {
        ModalContainer,
        ModalHeaderContainer,
        ModalFooterContainer,
        ModalHeaderContent,
        ModalBodyContainer,
        // @CUSTOM: Moved below nodes to the body
        // addToWishlist,
        // addToCart,
        // cartContainerProps
    } = props;

    return (
        <Module {...ModalContainer}>
                <Node {...ModalHeaderContainer}>{ModalHeaderContent}</Node>
                <Node {...ModalBodyContainer}>
                    {_renderBodyContent(props)}
                </Node>
                <Node {...ModalFooterContainer}>
                    {/* @CUSTOM: Moved the below TSX to the body */}
                    {/* <Node {...cartContainerProps}>
                        {addToCart && _renderAddToCart(addToCart)}
                        {addToWishlist && _renderAddToWishlist(addToWishlist)}
                    </Node> */}
                </Node>
        </Module>
    );
};

const _renderQuantity = (quantity: IBuyboxProductQuantityViewProps, quantityLimitsMessages: React.ReactNode): JSX.Element => {
    const { ContainerProps, LabelContainerProps, heading, input, errors } = quantity;

    return (
        <Node {...ContainerProps}>
            <Node {...LabelContainerProps}>
                {heading}
                {errors}
            </Node>
            {input}
            {quantityLimitsMessages}
        </Node>
    );
};

const _renderKeyInPrice = (keyInPrice: IBuyboxKeyInPriceViewProps): JSX.Element => {
    const { ContainerProps, LabelContainerProps, heading, input } = keyInPrice;

    return (
        <Node {...ContainerProps}>
            <Node {...LabelContainerProps}>{heading}</Node>
            {input}
        </Node>
    );
};

const _renderConfigure = (configure: IBuyboxProductConfigureViewProps): JSX.Element => {
    const { ContainerProps, dropdowns } = configure;

    return <Node {...ContainerProps}>{dropdowns.map(_renderConfigureDropdown)}</Node>;
};

const _renderBodyContent = (props: IQuickviewViewProps & IQuickviewProps<{}>): JSX.Element => { // @CUSTOM: Added extended definition props
    const {
        title,
        price,
        rating,
        keyInPrice,
        quantity,
        configure,
        inventoryLabel,
        quantityLimitsMessages,
        seeDetailsbutton,
        loading,
        ProductInfoContainerProps,
        MediaGalleryContainerProps,
        CarouselProps,
        unitOfMeasure,
        // @CUSTOM: Added the nodes below from the footer
        addToWishlist,
        addToCart,
        cartContainerProps,

    } = props;
    if (loading) {
        return <>{loading}</>;
    }
    //------------------------------------------------------
    // @CUSTOM: Getting product item ID for SKU display
    //------------------------------------------------------
    const product = props.productDetails?.product;
    const itemId = product?.ItemId;
    const skuDisplay = itemId && (
        <div className={cls('product-sku')}>
            <span className={cls('product-sku-label')}>{labelResources.sku}</span>
            <span className={cls('product-sku-value')}>{itemId}</span>
        </div>
    );
    //------------------------------------------------------

    //------------------------------------------------------
    // @CUSTOM: Getting product attributes for product
    // description display, uses itemId from above
    //------------------------------------------------------
    const [productAttr, productAttrLoaded] = React.useState({} as AttributesWithMetadata);
    itemId && React.useEffect(() => {
        const productAttributesInput = {
            ItemIds: [{ ItemId: itemId }],
            IncludeProductsFromDescendantCategories: false,
            SkipVariantExpansion: true,
            IncludeAttributes: true,
            Context: {
                ChannelId: props.context.request.apiSettings.channelId,
                CatalogId: getCatalogId(props.context.request)
            },
        };

        searchByCriteriaAsync({ callerContext: props.context.actionContext }, productAttributesInput).then((products) => {
            if (products.length) {
                const productSpecificationData = products[0].AttributeValues;
                const productAttributes = productSpecificationData && convertProductAttributes(productSpecificationData) || {};
                productAttrLoaded(productAttributes);
            }
        });
    }, []);

    const description = props.config.descriptionOverride && productAttr[props.config.descriptionOverride] || product?.Description;
    const descriptionDisplay = description && <p className={cls('product-description')}>{description}</p>;
    //------------------------------------------------------


    //------------------------------------------------------
    // @CUSTOM: Getting license data for blocking
    // duplicate purchase of license
    //------------------------------------------------------
    const customerData = props.data?.customerInformation?.result?.Attributes;
    const customerAttributes = customerData && convertCustomerAttributes(customerData);

    const isLicense = productAttr && (productAttr[attrNames.productType] === attrNames.troveAnnualLicense);
    const licenseParticipant = customerAttributes && customerAttributes[attrNames.troveParticipant];

    const licenseDuplicate = (isLicense && licenseParticipant);
    //------------------------------------------------------

    // @CUSTOM: signed out addtocart replacement
    const isSignedIn = props.context.request.user.isAuthenticated;

    return (
        <>
            <Node {...MediaGalleryContainerProps}>
                <Node {...CarouselProps} />
            </Node>
            <Node {...ProductInfoContainerProps}>
                {title}
                {/* @CUSTOM: Description Display */}
                {descriptionDisplay}
                {/* @CUSTOM: SKU Display */}
                {skuDisplay}
                {/* @CUSTOM: Moved seeDetailsbutton from above inventoryLabel */}
                {seeDetailsbutton}
                {price}
                {unitOfMeasure}
                {rating}
                {configure && _renderConfigure(configure)}
                {keyInPrice && _renderKeyInPrice(keyInPrice)}
                {inventoryLabel}

                {/* @CUSTOM: Duplicate license warning */}
                {licenseDuplicate && (
                    <div className='ms-buybox__license-duplicate'>
                        <RichTextComponent className='ms-buybox__license-duplicate-text' text={props.config.licenseDuplicate} />
                    </div>
                )}

                {/* @CUSTOM: Signed out addtocart replacement message */}
                {!licenseDuplicate && !isSignedIn && (
                    <div className='ms-buybox__signed-out-message'>
                        <RichTextComponent className='ms-buybox__signedout-message-text' text={props.config.signedOutMessage} />
                    </div>
                )}

                {/* @CUSTOM: add to cart success message */}
                {props.addedToCart && (
                    <div className='ms-buybox__item-added-message'>
                        <RichTextComponent className='ms-buybox__item-added-message-text' text={props.config.addedToCartMessage} />
                    </div>
                )}

                {/* @CUSTOM: Added the node below from the footer, and added the quantity that used to be below keyInPrice inside the node below */}
                <Node {...cartContainerProps}>
                    {!licenseDuplicate && isSignedIn && <div className='ms-quickView__action-group'>
                        {quantity && _renderQuantity(quantity, quantityLimitsMessages)}
                        {addToCart && _renderAddToCart(addToCart)}
                        {addToWishlist && _renderAddToWishlist(addToWishlist)}
                    </div>}
                </Node>
            </Node>
        </>
    );
};

/**
 * Functional component which renders quick view button and popup based by the given props file.
 * @param {IQuickviewViewProps} props - Configuration for the quick view component.
 * @returns {React.FC<IQuickviewViewProps>} - Functional component of the quick view.
 */
export const QuickViewFunctionalComponent: React.FC<IQuickviewViewProps & IQuickviewProps<{}>> = (props: IQuickviewViewProps & IQuickviewProps<{}>) => {    // @CUSTOM: Added extended definition props
    const { quickView, quickViewButton, isModalOpen, isMobileDevice } = props;
    return (
        <Module {...quickView}>
            {!isMobileDevice && quickViewButton}
            {isModalOpen && _renderQuickViewPopup(props)}
        </Module>
    );
};

export default QuickViewFunctionalComponent;